import HBIcon from "@/components/HBIcon";
import HBSwiper from "@/components/HBSwiper";
import { getRelatedBoards, getRelatedBoardsWeb } from "@/services";
import useReq from "@/utils/useReq";
import { last } from "lodash";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import BoardCardNew from "../BoardCardNew";
import styles from "../index.module.less";

interface BoardSwiperProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 画板数据列表
   */
  boardsData: any[];
  /**
   * 标题文本
   */
  title?: string;
  /**
   * 自定义样式类名
   */
  className?: string;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
  /**
   * 弹窗堆栈索引，用于生成唯一key
   */
  stackIndex?: number;
  /**
   * 当前pin的ID，用于添加viewSource参数
   */
  pinId?: string | number;

  /**
   * 当前pin
   */
  currentPin: IPin;

  /**
   * 点击"更多"的回调
   */
  onShowMore?: () => void;

  /**
   * 加载更多时的回调，用于追踪当前页数和累积的画板数据
   */
  onLoadMore?: (currentPage: number, allBoards: any[]) => void;

  /**
   * 是否使用新接口（全屏详情页使用新接口，旧版使用旧接口）
   */
  useNewApi?: boolean;

  /**
   * 获取下一个应该拉取的页码（用于与瀑布流共享页码）
   */
  getNextPage?: () => number;

  /**
   * 搜索词（用于埋点）
   */
  searchWord?: string;
}

function BoardSwiper({
  boardsData,
  title = "同在画板",
  className,
  style,
  stackIndex = 0,
  pinId,
  currentPin,
  onShowMore,
  onLoadMore,
  useNewApi = false,
  getNextPage,
  searchWord,
  ...restProps // 接收所有额外的 props（包括 data-* 属性）
}: BoardSwiperProps) {
  // 初始化 dataSource：如果有所在画板，直接包含进去，避免首次渲染高度不准确
  const initialDataSource = currentPin?.board
    ? [currentPin.board, ...boardsData]
    : boardsData;
  const [dataSource, setDataSource] = useState<IBoardInfo[]>(initialDataSource);
  const [currentPage, setCurrentPage] = useState(1); // 追踪当前已加载的页数

  const hasMoreRef = useRef(true);
  const totalCountRef = useRef(0); // 追踪总数
  const loadingRef = useRef(false); // 防止重复加载
  const swiperRef = useRef<any>(null);

  const { run: loadMore } = useReq(
    async () => {
      if (!hasMoreRef.current || loadingRef.current) {
        return;
      }

      loadingRef.current = true;

      let nextPage;
      if (useNewApi && getNextPage) {
        // 使用全局页码（与瀑布流共享）
        nextPage = getNextPage();
      } else {
        // 使用内部页码
        nextPage = currentPage + 1;
      }

      let result;
      if (useNewApi) {
        // 全屏详情页使用新接口（基于 page 分页）
        if (nextPage > 100) return; // 最大100页限制

        result = await getRelatedBoardsWeb({
          pin_id: pinId,
          limit: 20,
          page: nextPage,
        });
      } else {
        // 旧版详情页使用旧接口（基于 max 游标分页）
        const max = last(dataSource)?.seq || last(dataSource)?.board_id;
        result = await getRelatedBoards({
          pin_id: pinId,
          limit: 20,
          ...(max ? { max } : null),
        });
      }

      const newBoards = result?.boards || [];

      let updatedDataSource;
      setDataSource((prev) => {
        updatedDataSource = prev.concat(newBoards);
        return updatedDataSource;
      });

      // 根据接口类型判断是否还有更多数据
      if (useNewApi) {
        // 新接口：通过返回数组是否为空判断
        hasMoreRef.current = newBoards.length > 0;
      } else {
        // 旧接口：通过 more 字段判断
        hasMoreRef.current = result.more;
      }

      // 更新页数
      setCurrentPage(nextPage);

      // 调用父组件的回调，通知当前页数和累积的数据
      if (onLoadMore) {
        onLoadMore(nextPage, updatedDataSource);
      }

      loadingRef.current = false;
    },
    {
      manual: true,
      onError: () => {
        loadingRef.current = false;
      },
    }
  );

  useEffect(() => {
    // 重置状态（新的数据源）
    setCurrentPage(1);
    loadingRef.current = false;

    // 计算总画板数（包含「所在画板」）
    const totalBoardCount = currentPin?.board
      ? boardsData.length + 1
      : boardsData.length;

    // 更新 dataSource
    if (!currentPin.board) {
      setDataSource(boardsData);
      totalCountRef.current = boardsData.length;
    } else {
      setDataSource([currentPin.board, ...boardsData]);
      totalCountRef.current = totalBoardCount;
    }

    // 初始化 hasMore 状态：如果使用新接口，通过初始数据长度判断
    // 如果初始数据 < 20（limit），说明已经没有更多了
    if (useNewApi && boardsData.length < 20) {
      hasMoreRef.current = false;
    } else {
      hasMoreRef.current = true; // 重置为 true，可能还有更多
    }
  }, [boardsData, currentPin.board, useNewApi]);

  // 处理滚动事件，判断是否接近右边界
  const handleScroll = useCallback(
    (left: number) => {
      // left 是负数，表示内容向左移动的距离（即向右滚动）
      // 获取滚动容器的信息
      const swiperDom = swiperRef.current?.getScrollDom?.();
      if (!swiperDom) return;

      const viewWidth = swiperDom.offsetWidth; // 可视区域宽度
      // 从可视区域中找到 content DOM（使用子元素查找）
      const contentDom = swiperDom.children[0] as HTMLElement;
      if (!contentDom) return;

      const contentWidth = contentDom.offsetWidth; // 内容总宽度
      const scrolledDistance = Math.abs(left); // 已滚动距离（取绝对值）
      const maxScrollDistance = contentWidth - viewWidth; // 最大可滚动距离

      // 当滚动到距离右边界小于200px时，触发加载更多（只在向右滚动时）
      const threshold = 200;
      const distanceToEnd = maxScrollDistance - scrolledDistance;

      // 只有当 left 是负数（向右滚动）且接近右边界时才触发
      if (
        left < 0 &&
        distanceToEnd <= threshold &&
        hasMoreRef.current &&
        !loadingRef.current
      ) {
        loadMore();
      }
    },
    [loadMore]
  );

  // 判断是否显示"更多"入口：当前已有数据超过10个
  const showMoreButton = dataSource.length > 10;

  // 判断是否显示底部加载提示：只有当初始数据 > 3 时才显示
  // 注意：总数需要包含「所在画板」（如果有的话）
  const totalBoardCount = currentPin?.board
    ? boardsData.length + 1
    : boardsData.length;
  const showLoadingFooter = totalBoardCount > 3;

  // 判断是否应用 small 样式：使用稳定的 totalBoardCount 而不是动态的 dataSource.length
  // 避免在数据加载过程中因为数量变化导致布局闪烁
  // 只有当外部数据源显示总数 <= 3，且当前已加载的数据量达到总数时，才应用 small 样式
  // 这样可以避免在数据加载过程中因为数量变化导致布局闪烁
  const shouldApplySmall =
    totalBoardCount <= 3 && dataSource.length >= totalBoardCount;

  return (
    <div
      className={`${styles.boardSwiperContainer} ${className || ""}`}
      style={style}
      {...restProps} // 传递所有额外的 props 到根 div（包括 data-* 属性）
    >
      <div className={styles.boardSwiperHeader}>
        <div className={styles.boardSwiperTitle}>{title}</div>
        {showMoreButton && onShowMore && (
          <div
            className={styles.boardSwiperMore}
            onClick={onShowMore}
            data-gd-click="button_click"
            data-button-name="更多"
            data-button-source="采集详情页_普通采集_为你推荐"
            data-module-name="推荐画板"
          >
            更多
            <HBIcon
              className={styles.boardSwiperMoreIcon}
              type="ic_chevron_right"
            />
          </div>
        )}
      </div>
      <HBSwiper
        ref={swiperRef}
        className={styles.boardSwiper}
        contentClassName={`${styles.boardSwiperContent} ${
          shouldApplySmall ? styles.small : ""
        }`}
        iconClassName={styles.swiperIcon}
        hideGradient={true}
        onScroll={handleScroll}
      >
        {dataSource.map((board, index) => {
          const isCurrentBoard =
            index === 0 && board?.board_id === currentPin?.board?.board_id;
          return (
            <div
              key={`${stackIndex}-${board?.board_id || index}`}
              className={styles.boardSwiperCard}
            >
              <BoardCardNew
                record={board}
                hideButton={true}
                hideUser={true}
                location={index + 1}
                viewSource={isCurrentBoard ? pinId : undefined} // 只有「所在画板」才传递 viewSource
                stackIndex={stackIndex}
                searchWord={searchWord}
                isCurrentBoard={isCurrentBoard}
              />
            </div>
          );
        })}
        {showLoadingFooter && (
          <>
            {hasMoreRef.current ? (
              <div className={styles.loadingMore}>
                <div className={styles.loadingDot}>加载中...</div>
              </div>
            ) : (
              <div className={styles.noMore}>
                <div className={styles.noMoreText}>没有更多了</div>
              </div>
            )}
          </>
        )}
      </HBSwiper>
    </div>
  );
}

export default memo(BoardSwiper);
